import { ChefHat, PackageX, RotateCcw, Save } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import CleoPedidosPreferencias from "./CleoPedidosPreferencias";
import CleoPreferencias from "./CleoPreferencias";
import BlocoRecolhivel from "./BlocoRecolhivel";
import MesaQrPreferencias from "./MesaQrPreferencias";
import ModulosPreferencias from "./ModulosPreferencias";
import BalcaoConsumoPreferencias from "./BalcaoConsumoPreferencias";
import BalancaPreferencias from "./BalancaPreferencias";
import KdsDesfazerPreferencias from "./KdsDesfazerPreferencias";
import PreparoPreferencias from "./PreparoPreferencias";
import FechamentoPreferencias from "./FechamentoPreferencias";
import CaixaPreferencias from "./CaixaPreferencias";
import SenhaPreferencias from "./SenhaPreferencias";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const PADRAO = { limiar_amarelo: 18, limiar_vermelho: 28 };
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token") || ""}`, "Content-Type": "application/json" });

/** Configurações → Sistema → Preferências. Por enquanto: os minutos em que
 *  o card do KDS muda de cor (branco → amarelo → vermelho). */
export default function PreferenciasTab({ toast }) {
  const [autoEsgotar, setAutoEsgotar] = useState(null); // null = carregando
  useEffect(() => {
    fetch(`${API_URL}/api/settings/alertas`, { headers: auth(), cache: "no-store" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => setAutoEsgotar(!!d.auto_esgotar))
      .catch(() => setAutoEsgotar(false));
  }, []);
  const alternarAutoEsgotar = async () => {
    const novo = !autoEsgotar;
    setAutoEsgotar(novo);
    try {
      const r = await fetch(`${API_URL}/api/settings/alertas`, { method: "PUT", headers: auth(), body: JSON.stringify({ auto_esgotar: novo }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Não foi possível salvar.");
      toast({ title: novo ? "Esgotamento automático ligado" : "Esgotamento automático desligado",
        description: novo ? "Produtos sem estoque para a ficha foram marcados como indisponíveis e voltam sozinhos quando o estoque repor." : "Os produtos que a regra tinha esgotado voltaram ao cardápio." });
    } catch (e) {
      setAutoEsgotar(!novo);
      toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    }
  };
  const [valores, setValores] = useState(PADRAO);
  const [salvos, setSalvos] = useState(PADRAO);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/settings/kds`, { headers: auth(), cache: "no-store" })
      .then((r) => (r.ok ? r.json() : PADRAO))
      .then((d) => { setValores(d); setSalvos(d); })
      .catch(() => {})
      .finally(() => setCarregando(false));
  }, []);

  const am = Number(valores.limiar_amarelo) || 0;
  const vm = Number(valores.limiar_vermelho) || 0;
  const valido = am >= 1 && vm > am && vm <= 600;
  const mudou = am !== Number(salvos.limiar_amarelo) || vm !== Number(salvos.limiar_vermelho);

  const salvar = async () => {
    if (!valido || salvando) return;
    setSalvando(true);
    try {
      const r = await fetch(`${API_URL}/api/settings/kds`, { method: "PUT", headers: auth(), body: JSON.stringify({ limiar_amarelo: am, limiar_vermelho: vm }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Não foi possível salvar.");
      setSalvos({ limiar_amarelo: d.limiar_amarelo, limiar_vermelho: d.limiar_vermelho });
      toast({ title: "Preferências salvas", description: `KDS: branco até ${am} min, amarelo até ${vm} min, vermelho depois. A tela da cozinha atualiza em até 1 minuto.` });
    } catch (e) {
      toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    } finally {
      setSalvando(false);
    }
  };

  const campo = (chave, rotulo, ajuda) => (
    <div>
      <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{rotulo}</Label>
      <div className="relative mt-1.5">
        <Input type="number" min={1} max={600} inputMode="numeric" value={valores[chave]}
          onChange={(e) => setValores((v) => ({ ...v, [chave]: e.target.value }))}
          className="h-11 pr-14 text-lg font-bold tabular-nums" />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">min</span>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">{ajuda}</p>
    </div>
  );

  // Faixa de preview: proporcional aos limiares, com o vermelho ocupando o
  // mesmo tanto do trecho amarelo pra nao sumir quando os numeros sao grandes.
  const largAm = am, largVm = Math.max(vm - am, 1), largVer = Math.max(vm - am, 1);
  const total = largAm + largVm + largVer;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-6">
      {/* Seções com blocos recolhíveis (pedido do dono, 29/09/2026): cada bloco
          mostra título, resumo e o switch principal; o conteúdo abre no clique. */}
      <Secao titulo="Operação">
        <ModulosPreferencias toast={toast} />
        <BalcaoConsumoPreferencias toast={toast} />
        <SenhaPreferencias toast={toast} />
        <MesaQrPreferencias toast={toast} />
        <BalancaPreferencias toast={toast} />
      </Secao>

      <Secao titulo="Caixa">
        <CaixaPreferencias toast={toast} />
      </Secao>

      <Secao titulo="Impressão">
        <PreparoPreferencias toast={toast} />
        <FechamentoPreferencias toast={toast} />
      </Secao>

      <Secao titulo="Atendimento">
        <CleoPedidosPreferencias toast={toast} />
        <CleoPreferencias toast={toast} />
      </Secao>

      <Secao titulo="Estoque e cozinha">
        <BlocoRecolhivel e2e="card-auto-esgotar" icone={PackageX} titulo="Esgotar produtos com base no estoque"
          carregando={autoEsgotar === null}
          resumo={autoEsgotar === null ? "Carregando…" : autoEsgotar ? "Ligado — produto sem estoque para a ficha fica indisponível" : "Desligado"}
          controle={
            <button type="button" onClick={alternarAutoEsgotar} disabled={autoEsgotar === null} data-e2e="sw-auto-esgotar"
              role="switch" aria-checked={!!autoEsgotar} aria-pressed={!!autoEsgotar}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${autoEsgotar ? "bg-primary" : "bg-gray-300 dark:bg-gray-600"} disabled:opacity-50`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoEsgotar ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          }>
          <p className="text-sm text-muted-foreground">
            Quando o estoque de um ingrediente não cobre a ficha técnica de um produto, ele é marcado como indisponível no cardápio
            e avisado no WhatsApp. Ao repor o estoque, volta sozinho. Produto esgotado na mão não é mexido.
          </p>
        </BlocoRecolhivel>

        <BlocoRecolhivel e2e="card-kds-cores" icone={ChefHat} titulo="Cores do KDS por tempo"
          carregando={carregando}
          resumo={carregando ? "Carregando…" : `Amarelo a partir de ${salvos.limiar_amarelo} min · vermelho a partir de ${salvos.limiar_vermelho} min`}>
        <div className="space-y-5">
        <p className="text-sm text-muted-foreground">Quanto tempo o pedido pode ficar na cozinha antes de o card mudar de cor. Conta desde que ele entra em produção e não zera ao ir para Pronto.</p>
        {carregando ? <p className="text-sm text-muted-foreground">Carregando...</p> : (<>
          <div className="grid sm:grid-cols-2 gap-4">
            {campo("limiar_amarelo", "Fica AMARELO a partir de", "Até esse tempo o card é branco (tranquilo).")}
            {campo("limiar_vermelho", "Fica VERMELHO a partir de", "Entre os dois, amarelo (médio). Depois, vermelho com selo ATRASO.")}
          </div>

          <div>
            <div className="flex h-9 rounded-lg overflow-hidden border text-[11px] font-bold">
              <div className="bg-white text-zinc-900 flex items-center justify-center" style={{ width: `${(largAm / total) * 100}%` }}>Branco · 0–{am} min</div>
              <div className="bg-yellow-300 text-zinc-900 flex items-center justify-center" style={{ width: `${(largVm / total) * 100}%` }}>Amarelo · {am}–{vm}</div>
              <div className="bg-red-500 text-white flex items-center justify-center" style={{ width: `${(largVer / total) * 100}%` }}>Vermelho · {vm}+</div>
            </div>
            {!valido && <p className="text-xs text-red-500 mt-1.5">O tempo do amarelo precisa ser menor que o do vermelho (entre 1 e 600 minutos).</p>}
          </div>

          <div className="flex items-center gap-2 justify-end">
            <Button variant="outline" className="h-10" onClick={() => setValores(PADRAO)} disabled={am === PADRAO.limiar_amarelo && vm === PADRAO.limiar_vermelho}>
              <RotateCcw className="w-4 h-4 mr-1.5" /> Restaurar padrão (18 / 28)
            </Button>
            <Button className="h-10 font-bold" onClick={salvar} disabled={!valido || !mudou || salvando}>
              <Save className="w-4 h-4 mr-1.5" /> {salvando ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </>)}
        </div>
        </BlocoRecolhivel>

        <KdsDesfazerPreferencias toast={toast} />
      </Secao>
    </div>
  );
}

// Título de seção da aba (Operação, Impressão, Atendimento, Estoque e cozinha).
function Secao({ titulo, children }) {
  return (
    <section className="space-y-3" data-e2e="secao-preferencias">
      <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-1">{titulo}</h2>
      {children}
    </section>
  );
}
